import React, {
  CSSProperties,
  ReactNode,
  memo,
  useMemo,
} from 'react';
import cx from 'classnames';
import s from './BAvatarAvatar.module.scss';
import notAvatar from './not-avatar.png';
import exchangerAvatar from './exchanger-avatar.png';
import moderatorAvatar from './moderator-avatar.png';

type AvatarVariant = 'circular' | 'rounded' | 'square';
type AvatarRole = 'ROLE_CLIENT' | 'ROLE_MODERATOR' | 'ROLE_EXCHANGER' | 'ROLE_PARTNER';

export interface BAvatarProps {
  className?: string;
  src?: string;
  size?: number;
  variant?: AvatarVariant;
  children?: string | ReactNode;
  role?: AvatarRole;
}

export const BAvatar = memo<BAvatarProps>((props: BAvatarProps) => {
  const {
    className,
    src,
    role = 'ROLE_CLIENT',
    size = 50,
    variant = 'circular',
    children,
  } = props;

  const isExchanger = useMemo(() => role === 'ROLE_EXCHANGER', [role]);
  const isModerator = useMemo(() => role === 'ROLE_MODERATOR', [role]);

  const childrenData = useMemo(
    () => (typeof children === 'string' && children.length >= 1
      ? children[0]
      : children),
    [children],
  );

  const style = useMemo<CSSProperties>(() => ({
    width: size,
    height: size,
  }), [size]);

  const additional = [
    s[variant],
    className,
  ];

  if (isExchanger) {
    return (
      <div className={cx(s.avatarRoot, additional)} style={style}>
        <img src={exchangerAvatar} alt="exchanger" className={s.avatarImg} />
      </div>
    );
  }

  if (isModerator) {
    return (
      <div className={cx(s.avatarRoot, additional)} style={style}>
        <img src={moderatorAvatar} alt="moderator" className={s.avatarImg} />
      </div>
    );
  }

  if (children) {
    return (
      <div className={cx(s.avatarWithChildren, additional)} style={style}>
        {childrenData}
      </div>
    );
  }

  return (
    <div className={cx(s.avatarRoot, additional)} style={style}>
      {src
        ? <img src={src} alt="avatar" className={s.avatarImg} />
        : <img src={notAvatar} alt="not-avatar" className={s.avatarImg} />}
    </div>
  );
});
